import { useCallback, useContext, useEffect, useRef } from "react"

import { getOptionValues } from 'app/components/commonFilters/hooks'
import { CommonFiltersItemsContext } from '../context'


export default function useCommonFiltersItems() {
    const { state, setState } = useContext(CommonFiltersItemsContext)
    const { dateRange, maxDate } = state

    const mountedRef = useRef(true)
    useEffect(() => { return () => { mountedRef.current = false } }, [])

	const updateState = useCallback(newState => {
        if (!mountedRef.current) return
        setState(state => ({ ...state, ...newState}))
	}, [setState])

	const setHotels = useCallback(op => {
        if (!mountedRef.current) return
        const hotels = getOptionValues(op)
        // A hotel taken out of the selection is no longer in the report: drop it from the excluded ones too.
        // Empty selection = all hotels, so nothing to drop.
        setState(state => ({
            ...state,
            hotels,
            excludedHotels: Array.isArray(hotels) && hotels.length && state.excludedHotels?.length
                ? state.excludedHotels.filter(id => hotels.some(h => String(h) === String(id)))
                : state.excludedHotels
        }))
	}, [setState])

    const setHotelHistorical = useCallback(op => {
		updateState({ hotelHistorical: getOptionValues(op) })
	}, [updateState])

    const setHotelsRealTime = useCallback(op => {
        if (Array.isArray(op))
            updateState({ hotelsRealTime: getOptionValues(op) })
    }, [updateState])

    const setYear = useCallback(op => {
		updateState({ year: getOptionValues(op) })
	}, [updateState])

    const setAllHotels = useCallback(op => {
		updateState({ allHotels: getOptionValues(op), hotelName: op?.label })
	}, [updateState])

    const setHotelChain = useCallback(op => {
		updateState({ hotelChain: getOptionValues(op) })
	}, [updateState])

	const setDateRange = useCallback(op => {
        updateState({ dateRange: getOptionValues(op) })
    }, [updateState])

	const setCustomDateRange = useCallback(newValue => {
        updateState({ customDateRange: newValue })
    }, [updateState])

    const setCustomDate = useCallback(newValue => {
        updateState({ customDate: newValue })
    }, [updateState])

    const setSpecificDays = useCallback(newValue => {
        updateState({ specificDays: newValue })
    }, [updateState])

    const setRealtimeMarket = useCallback(op => {
        updateState({ realtimeMarket: getOptionValues(op) })
    }, [updateState])

    const setCalendarType = useCallback(op => {
        updateState({ calendarType: getOptionValues(op) })
    }, [updateState])

	const setMaxDate = useCallback(ops => {
		updateState({ maxDate: ops.length > 1 ? ops[ops.length - 2].value : null })
		return ops
	}, [updateState])

	const setBoard = useCallback(op => {
        updateState({ boardID: getOptionValues(op) })
    }, [updateState])

    const setRate = useCallback(op => {
        updateState({ rateID: getOptionValues(op) })
    }, [updateState])

    const setProviders = useCallback(op => {
        updateState({ providerID: getOptionValues(op) })
    }, [updateState])

    const setOtherSites = useCallback(op => {
        updateState({ otherSites: getOptionValues(op), sites: op.value })
    }, [updateState])

    const setSites = useCallback(op => {
        updateState({ sites: getOptionValues(op), otherSites: null })
    }, [updateState])

    const setTotalSites = useCallback(op => {
        updateState({ totalSites: op })
    }, [updateState])

    const setElementsOtherSites = useCallback(op => {
        updateState({ elementsOtherSites: op })
    }, [updateState])

    const setSiteLocation = useCallback(op => {
        updateState({ siteLocation: getOptionValues(op) })
    }, [updateState])

    const setLocation = useCallback(op => {
        updateState({ location: getOptionValues(op) })
    }, [updateState])

    const setSitesList = useCallback(op => {
        updateState({ sitesList: getOptionValues(op), siteName: Array.isArray(op) ? op.map(a => a.label).join(', ') : op.label })
    }, [updateState])

    const setSitesListRealTime = useCallback(op => {
        if (Array.isArray(op))
            updateState({ sitesListRealTime: getOptionValues(op), siteName: Array.isArray(op) ? op.map(a => a.label).join(', ') : op.label })
    }, [updateState])

    const setSources = useCallback(op => {
        updateState({ sources: getOptionValues(op) })
    }, [updateState])

    const setRooms = useCallback(op => {
        updateState({ roomID: getOptionValues(op) })
    }, [updateState])

    const setRawRooms = useCallback(op => {
        updateState({ rawRooms: op })
    }, [updateState])

    const setAdults = useCallback(op => {
        updateState({ adults: getOptionValues(op) })
    }, [updateState])

    const setKids = useCallback(op => {
        updateState({ kids: op.value !== 0 ? getOptionValues(op) : 0 })
    }, [updateState])

    const setOccupancy = useCallback(op => {
        updateState({ occupancy: getOptionValues(op) })
    }, [updateState])

    const setDefaultOccupancy = useCallback(op => {
		updateState({ defaultOccupancy: getOptionValues(op) })
	}, [updateState])

    const setDefaultNights = useCallback(op => {
		updateState({ defaultNights: getOptionValues(op) })
	}, [updateState])

    const setDefaultOtas = useCallback(op => {
		updateState({ defaultOtas: getOptionValues(op) })
	}, [updateState])

    const setNights = useCallback(op => {
        updateState({ nights: getOptionValues(op), noNights: op ? op.isFalse : false })
    }, [updateState])

    const setNightsStatic = useCallback(op => {
        updateState({ nightsStatic: getOptionValues(op) })
    }, [updateState])

	const setPriceType = useCallback(op => {
        updateState({ priceType: getOptionValues(op) })
    }, [updateState])

	const setOpen = useCallback(op => {
        updateState({ open: op })
    }, [updateState])

    const setDevices = useCallback(op => {
        if (Array.isArray(op) && op?.length > 0)
            updateState({ devices: getOptionValues(op)})
        else if (!Array.isArray(op))
            updateState({ devices: op.value })
    }, [updateState])

    const setDevicesStatic = useCallback(op => {
        updateState({ devicesStatic: parseInt(getOptionValues(op)) })
    }, [updateState])

    const setPayType = useCallback(op => {
        updateState({ payType: getOptionValues(op) })
    }, [updateState])

	const setMinStay = useCallback(newValue => {
        updateState({ minStay: newValue.target.checked })
    }, [updateState])

    const setCityFrom = useCallback(op => {
        updateState({ cityFrom: getOptionValues(op), cityFromName: op.label })
    }, [updateState])

    const setCityTo = useCallback(op => {
        updateState({ cityTo: getOptionValues(op), cityToName: op.label })
    }, [updateState])

    const setConnectivities = useCallback(op => {
        updateState({ connectivities: getOptionValues(op) })
        //getSitesFromRoute(op.value)
    }, [updateState, /*getSitesFromRoute*/])

    const setMappingType = useCallback(op => {
        updateState({ mappingType: getOptionValues(op) })
    }, [updateState])

    const setProvince = useCallback(op => {
        updateState({ province: getOptionValues(op) })
    }, [updateState])

    const setIdMainHotelCustomPinned = useCallback(idHotel => {
        updateState({ idMainHotelCustomPinned: idHotel })
    }, [updateState])

    const setExcludedHotels = useCallback(idHotels => {
        updateState({ excludedHotels: Array.isArray(idHotels) ? idHotels : [] })
    }, [updateState])

	return {
		hotels: state.hotels, allHotels: state.allHotels, hotelName: state.hotelName, specificDays: state.specificDays,
		dateRange, maxDate, customDateRange: state.customDateRange, mappingType: state.mappingType, location: state.location,
		boardID: state.boardID, rateID: state.rateID, providerID: state.providerID, noNights: state.noNights,
		roomID: state.roomID, adults: state.adults, kids: state.kids, occupancy: state.occupancy, sites: state.sites,
        sitesList: state.sitesList, nights: state.nights, priceType: state.priceType, open: state.open, devices: state.devices,
        payType: state.payType, sources: state.sources, siteName: state.siteName, connectivities: state.connectivities,
        hotelChain: state.hotelChain, siteLocation: state.siteLocation, defaultOccupancy: state.defaultOccupancy,
        customDate: state.customDate, minStay: state.minStay, rawRooms: state.rawRooms, cityFrom: state.cityFrom,
        cityTo: state.cityTo, cityFromName: state.cityFromName, cityToName: state.cityToName, year: state.year,
        hotelHistorical: state.hotelHistorical, otherSites: state.otherSites, province: state.province,
        elementsOtherSites: state.elementsOtherSites, devicesStatic: state.devicesStatic, defaultNights: state.defaultNights,
        nightsStatic: state.nightsStatic, calendarType: state.calendarType, sitesListRealTime: state.sitesListRealTime,
        hotelsRealTime: state.hotelsRealTime, defaultOtas: state.defaultOtas, totalSites: state.totalSites,
        realtimeMarket: state.realtimeMarket, idMainHotelCustomPinned: state.idMainHotelCustomPinned,
        excludedHotels: state.excludedHotels,

		updateState, setHotels, setDateRange, setCustomDateRange, setMaxDate, setBoard, setRate,
        setProviders, setRooms, setAdults, setKids, setOccupancy, setSites, setNights, setNightsStatic, setOpen,
        setPriceType, setDevices, setPayType, setSitesList, setSources, setConnectivities, setHotelChain, setSiteLocation,
        setAllHotels, setMappingType, setDefaultOccupancy, setCustomDate, setMinStay, setTotalSites,
        setCityFrom, setCityTo, setRawRooms, setLocation, setHotelHistorical, setYear, setOtherSites, setElementsOtherSites,
        setDevicesStatic, setDefaultNights, setProvince, setSpecificDays, setCalendarType, setSitesListRealTime,
        setHotelsRealTime, setDefaultOtas, setRealtimeMarket, setIdMainHotelCustomPinned, setExcludedHotels
	}
}